import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  UserIcon, 
  GlobeAltIcon, 
  PhoneIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import SearchableSelect from '../components/SearchableSelect';
import api from '../services/api';
import toast from '../utils/toast';

const Profile = () => {
  const { user, organization } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    country: '',
    website: ''
  });
  const [originalData, setOriginalData] = useState({});
  const [errors, setErrors] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  const countries = [
    { value: 'GH', label: 'Ghana', icon: '🇬🇭', description: 'West Africa' },
    { value: 'NG', label: 'Nigeria', icon: '🇳🇬', description: 'West Africa' },
    { value: 'KE', label: 'Kenya', icon: '🇰🇪', description: 'East Africa' },
    { value: 'ZA', label: 'South Africa', icon: '🇿🇦', description: 'Southern Africa' },
    { value: 'EG', label: 'Egypt', icon: '🇪🇬', description: 'North Africa' },
    { value: 'US', label: 'United States', icon: '🇺🇸', description: 'North America' },
    { value: 'GB', label: 'United Kingdom', icon: '🇬🇧', description: 'Europe' },
    { value: 'CA', label: 'Canada', icon: '🇨🇦', description: 'North America' },
    { value: 'DE', label: 'Germany', icon: '🇩🇪', description: 'Europe' },
    { value: 'FR', label: 'France', icon: '🇫🇷', description: 'Europe' },
    { value: 'IN', label: 'India', icon: '🇮🇳', description: 'South Asia' },
    { value: 'BR', label: 'Brazil', icon: '🇧🇷', description: 'South America' },
    { value: 'MX', label: 'Mexico', icon: '🇲🇽', description: 'North America' },
    { value: 'AU', label: 'Australia', icon: '🇦🇺', description: 'Oceania' },
    { value: 'other', label: 'Other', icon: '🌍', description: 'Other country' }
  ];

  const fetchUserProfile = useCallback(async () => {
    try {
      setLoadingProfile(true);
      const response = await api.getUserProfile();
      const { user: userData, organization: orgData } = response.data;
      
      const profileData = {
        firstName: userData.firstName || user?.firstName || '',
        lastName: userData.lastName || user?.lastName || '',
        email: userData.email || user?.email || '',
        phoneNumber: userData.phone || '',
        country: orgData.country || '',
        website: orgData.website || ''
      };
      
      setFormData(profileData);
      setOriginalData(profileData);
    } catch (error) {
      console.error('Error fetching profile:', error);
      
      // Fallback to AuthContext data if API fails
      if (user || organization) {
        const fallbackData = {
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          phoneNumber: '',
          country: '',
          website: ''
        };
        setFormData(fallbackData);
        setOriginalData(fallbackData);
        toast.error('Using cached profile data - some fields may be incomplete');
      } else {
        toast.error('Failed to load profile data');
      }
    } finally {
      setLoadingProfile(false);
    }
  }, [user, organization]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  useEffect(() => {
    const changed = Object.keys(formData).some(key => 
      formData[key] !== originalData[key]
    );
    setHasChanges(changed);
  }, [formData, originalData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.country) newErrors.country = 'Country is required';
    
    // Phone number validation
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else {
      const phoneNumber = formData.phoneNumber.replace(/\s+/g, '');
      
      if (formData.country === 'GH') {
        // Ghana phone validation: +233 followed by 9 digits or 0 followed by 9 digits
        if (!/^(\+233|0)[2-9]\d{8}$/.test(phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid Ghana phone number (e.g., +233 24 123 4567 or 024 123 4567)';
        }
      } else if (formData.country === 'NG') {
        // Nigeria phone validation: +234 followed by 10 digits or 0 followed by 10 digits
        if (!/^(\+234|0)[7-9]\d{9}$/.test(phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid Nigeria phone number (e.g., +234 803 123 4567 or 0803 123 4567)';
        }
      } else {
        // Basic international phone validation for other countries
        if (!/^\+?[\d\s-()]{8,15}$/.test(phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid phone number';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});
    
    try {
      await api.updateUserProfile(formData);
      
      toast.success('Profile updated successfully!');
      setOriginalData({ ...formData });
      
    } catch (error) {
      console.error('Profile update error:', error);
      setErrors({ submit: 'Failed to update profile. Please try again.' });
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ ...originalData });
    setErrors({});
  };

  const isProfileComplete = () => {
    return formData.firstName && formData.lastName && formData.country && formData.phoneNumber;
  };

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-whatsapp-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Account Profile</h1>
                <p className="text-sm text-gray-600">Manage your account information and preferences</p>
              </div>
              <div className="flex items-center space-x-2">
                {isProfileComplete() ? (
                  <div className="flex items-center text-green-600">
                    <CheckCircleIcon className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">Complete</span>
                  </div>
                ) : (
                  <div className="flex items-center text-yellow-600">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
                    <span className="text-sm font-medium">Incomplete</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Profile Completion Banner */}
          {!isProfileComplete() && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="font-medium">Complete your profile</span> to unlock all features and create WhatsApp agents.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center">
              <PencilIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
            {errors.submit && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {errors.submit}
              </div>
            )}

            {/* Personal Information */}
            <div className="space-y-6">
              <h3 className="text-base font-medium text-gray-900 border-b border-gray-200 pb-2">
                Personal Information
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                        errors.firstName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="John"
                    />
                  </div>
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                        errors.lastName ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Doe"
                    />
                  </div>
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    readOnly
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                        errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder={
                        formData.country === 'GH' ? '+233 24 123 4567' :
                        formData.country === 'NG' ? '+234 803 123 4567' :
                        '+233 24 123 4567'
                      }
                    />
                  </div>
                  {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
                </div>
              </div>
            </div>

            {/* Organization Information */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <SearchableSelect
                  label="Country"
                  name="country"
                  options={countries}
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                  placeholder="Search or select country..."
                  isRequired={true}
                  error={errors.country}
                  isSearchable={true}
                  isClearable={true}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <div className="relative">
                    <GlobeAltIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                      placeholder="https://yourcompany.com"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={resetForm}
                disabled={!hasChanges || loading}
                className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Changes
              </button>

              <button
                type="submit"
                disabled={loading || !hasChanges}
                className="px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  'Save Profile'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
